// @ts-check
/** @typedef {import('../../types/api').SiteSetting} SiteSetting */

import { Save, Sparkles, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminSeoService from '../../services/adminSeoService';
import adminSiteSettingsService from '../../services/adminSiteSettingsService';
import FileUpload from '../FileUpload';
import LoadingScreen from '../LoadingScreen';

const TITLE_MAX = 60;
const DESC_MAX = 160;
const SITE_URL = 'https://kaay.co';

/**
 * Site-wide keys. The storefront (frontend-next/src/lib/seo.ts) reads the same keys
 * and falls back to its hardcoded copy when a value is blank.
 */
const SITE_FIELDS = [
  {
    key: 'seo_site_name',
    label: 'Site name',
    hint: 'Appended to every page title as "Page — Site name" and used as the brand name in structured data.',
    max: 30,
  },
  {
    key: 'seo_home_title',
    label: 'Homepage title',
    hint: 'The blue headline Google shows for kaay.co. Brand first, then a short promise.',
    max: TITLE_MAX,
  },
  {
    key: 'seo_home_description',
    label: 'Homepage description',
    hint: 'Grey text under the homepage result.',
    max: DESC_MAX,
    multiline: true,
  },
  {
    key: 'seo_default_description',
    label: 'Default description',
    hint: 'Used by any page without its own description.',
    max: DESC_MAX,
    multiline: true,
  },
  {
    key: 'seo_organization_description',
    label: 'Brand description (structured data)',
    hint: 'Who KAAY is, for Google Knowledge Panel and AI search. Not shown on the site.',
    max: 300,
    multiline: true,
  },
  {
    key: 'seo_social_instagram',
    label: 'Instagram URL',
    hint: 'Linked from structured data so Google connects the profile to the brand.',
    max: 200,
  },
  { key: 'seo_social_pinterest', label: 'Pinterest URL', hint: '', max: 200 },
  { key: 'seo_social_facebook', label: 'Facebook URL', hint: '', max: 200 },
  {
    key: 'seo_social_linkedin',
    label: 'LinkedIn URL',
    hint: 'Use the public company page, not the admin dashboard link.',
    max: 200,
  },
];

/**
 * Mirrors SEO_PAGE_KEYS in frontend-next/src/lib/seo.ts.
 * `summary` is what the AI is told the page shows; it never invents beyond this.
 */
const PAGE_FIELDS = [
  {
    key: 'about',
    path: '/about',
    label: 'Our Story',
    summary:
      'The brand story: who founded KAAY, the design philosophy of dresses built to move, and how pieces are made.',
  },
  {
    key: 'dresses',
    path: '/dresses',
    label: 'Dresses',
    summary: 'All KAAY dresses, filterable by length (mini, midi, maxi), occasion, mood and place.',
  },
  {
    key: 'ready_to_wear',
    path: '/ready-to-wear',
    label: 'Ready to Wear',
    summary: 'The complete ready-to-wear collection: every dress, set and separate currently available.',
  },
  {
    key: 'sets_separates',
    path: '/sets-separates',
    label: 'Sets & Separates',
    summary: 'Co-ord sets, tops, bottoms and jumpsuits designed to be worn together or mixed.',
  },
  {
    key: 'lookbook',
    path: '/lookbook',
    label: 'Lookbook',
    summary: 'Editorial lookbook imagery styling the current collection.',
  },
  {
    key: 'contact',
    path: '/contact',
    label: 'Contact',
    summary: 'How to reach the KAAY concierge for orders, sizing and styling help.',
  },
  { key: 'faqs', path: '/faqs', label: 'FAQs', summary: 'Answers on shipping, returns, sizing, orders and care.' },
  {
    key: 'size_guide',
    path: '/size-guide',
    label: 'Size Guide',
    summary: 'Size chart with body and garment measurements, plus a find-my-size recommender.',
  },
  {
    key: 'shipping',
    path: '/shipping',
    label: 'Shipping',
    summary: 'Shipping destinations, delivery times and costs for international orders.',
  },
  {
    key: 'returns',
    path: '/returns',
    label: 'Returns & Exchanges',
    summary: 'Return and exchange policy, return window and how to start a return.',
  },
  {
    key: 'customs_duties_taxes',
    path: '/customs-duties-taxes',
    label: 'Customs, Duties & Taxes',
    summary: 'What international customers pay in duties and taxes and how they are collected.',
  },
];

const HOME_SUMMARY =
  'The homepage: hero campaign, new collection, featured dresses, sets and separates, and the brand story.';
const DEFAULT_BRAND_CONTEXT =
  'KAAY is a luxury womenswear brand offering contemporary dresses and ready-to-wear clothing. Pieces designed to move, sit, and live in — refined wearable luxury for women who value quality and considered design.';

const OG_IMAGE_KEY = 'seo_og_image';

const ALL_KEYS = [
  ...SITE_FIELDS.map((f) => f.key),
  OG_IMAGE_KEY,
  ...PAGE_FIELDS.flatMap((p) => [`seo_${p.key}_title`, `seo_${p.key}_description`]),
];

const counterClass = (len, max) =>
  len > max
    ? 'text-red-600 dark:text-red-400'
    : len > max * 0.9
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-muted-foreground/70';

/** @param {{ label: string, hint?: string, value: string, max: number, multiline?: boolean, onChange: (v: string) => void, placeholder?: string }} props */
const Field = ({ label, hint, value, max, multiline, onChange, placeholder }) => {
  const len = (value || '').length;
  const cls =
    'w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground focus:border-foreground';
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <label className="text-sm font-medium text-foreground/80">{label}</label>
        <span className={`text-xs ${counterClass(len, max)}`}>
          {len}/{max}
        </span>
      </div>
      {multiline ? (
        <textarea
          rows={2}
          className={cls}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <input
          type="text"
          className={cls}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
};

const GenerateButton = ({ busy, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="flex items-center gap-2 px-3 py-1.5 text-xs border border-input rounded-md hover:bg-muted/50 disabled:opacity-50 shrink-0"
    title="Draft the description (and the title, if blank) with AI. Review before saving."
  >
    <Sparkles className={`w-3 h-3 ${busy ? 'animate-pulse' : ''}`} />
    {busy ? 'Generating…' : 'Generate with AI'}
  </button>
);

/** Approximation of a Google desktop result. */
const SnippetPreview = ({ url, title, description, siteName }) => (
  <div className="bg-card border border-border rounded-lg p-4 font-sans">
    <div className="flex items-center gap-2 mb-1">
      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground">
        {(siteName || 'K').charAt(0).toUpperCase()}
      </div>
      <div className="leading-tight">
        <div className="text-sm text-foreground">{siteName || 'KAAY'}</div>
        <div className="text-xs text-muted-foreground">{url}</div>
      </div>
    </div>
    <div className="text-lg text-[#1a0dab] truncate">{title || 'Page title'}</div>
    <div className="text-sm text-muted-foreground line-clamp-2">
      {description || 'Meta description will appear here.'}
    </div>
  </div>
);

const SiteSeoTab = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /** @type {[Record<string, string>, Function]} */
  const [values, setValues] = useState({});
  /** @type {[Record<string, string>, Function]} */
  const [saved, setSaved] = useState({});
  const [showOgUpload, setShowOgUpload] = useState(false);
  /** page key currently being generated ('home' or a PAGE_FIELDS key) */
  const [generating, setGenerating] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await adminSiteSettingsService.getAllSettings();
      const next = {};
      for (const s of all) if (ALL_KEYS.includes(s.key)) next[s.key] = s.value || '';
      setValues(next);
      setSaved(next);
    } catch (err) {
      toast.error(err.message || 'Failed to load SEO settings');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => ALL_KEYS.filter((k) => (values[k] || '') !== (saved[k] || '')), [values, saved]);

  const handleSave = async () => {
    if (dirty.length === 0) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(dirty.map((k) => [k, values[k] || '']));
      await adminSiteSettingsService.bulkUpdate(payload);
      setSaved((prev) => ({ ...prev, ...payload }));
      toast.success('SEO settings saved. Live site updates within an hour.');
    } catch (err) {
      toast.error(err.message || 'Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Draft title + description for one page. Always fills the description;
   * only fills the title when it is blank so a hand-written title survives.
   */
  const handleGenerate = async (page) => {
    const isHome = page.key === 'home';
    const tKey = isHome ? 'seo_home_title' : `seo_${page.key}_title`;
    const dKey = isHome ? 'seo_home_description' : `seo_${page.key}_description`;
    setGenerating(page.key);
    try {
      const result = await adminSeoService.generate({
        kind: 'page',
        name: isHome ? `${values.seo_site_name || 'KAAY'} homepage` : page.label,
        path: page.path,
        short_description: page.summary,
        description: values[dKey] || undefined,
        brand_context: values.seo_organization_description || DEFAULT_BRAND_CONTEXT,
      });
      if (result.seo_description) set(dKey, result.seo_description.slice(0, DESC_MAX));
      if (result.seo_title && !(values[tKey] || '').trim()) set(tKey, result.seo_title.slice(0, TITLE_MAX));
    } catch (err) {
      toast.error(err.message || 'Failed to generate SEO copy');
    } finally {
      setGenerating('');
    }
  };

  if (loading) return <LoadingScreen />;

  const siteName = values.seo_site_name || 'KAAY';

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Titles and descriptions Google shows for the homepage and each main page. Blank fields keep the built-in
          defaults. Products and collections are edited in their own tabs.
        </p>
        <button
          onClick={handleSave}
          disabled={saving || dirty.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg disabled:opacity-40 shrink-0"
        >
          <Save className="w-4 h-4" />
          {saving
            ? 'Saving…'
            : dirty.length > 0
              ? `Save ${dirty.length} change${dirty.length === 1 ? '' : 's'}`
              : 'Saved'}
        </button>
      </div>

      {/* Site-wide */}
      <section className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold">Site &amp; Homepage</h3>
          <GenerateButton
            busy={generating === 'home'}
            onClick={() => handleGenerate({ key: 'home', label: 'Homepage', path: '/', summary: HOME_SUMMARY })}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            {SITE_FIELDS.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                hint={f.hint}
                max={f.max}
                multiline={f.multiline}
                value={values[f.key]}
                onChange={(v) => set(f.key, v)}
              />
            ))}
          </div>
          <div className="space-y-4">
            <SnippetPreview
              url={SITE_URL}
              siteName={siteName}
              title={values.seo_home_title}
              description={values.seo_home_description}
            />

            <div>
              <label className="text-sm font-medium text-foreground/80 block mb-1">Share image (Open Graph)</label>
              <p className="text-xs text-muted-foreground mb-2">
                Shown when kaay.co is shared on WhatsApp, Instagram, Pinterest, and in Google&apos;s logo slot. 1200 ×
                630 px.
              </p>
              {values[OG_IMAGE_KEY] && !showOgUpload && (
                <div className="flex items-start gap-3">
                  <img
                    src={values[OG_IMAGE_KEY]}
                    alt="Share preview"
                    className="w-48 rounded-lg border border-border object-cover"
                  />
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setShowOgUpload(true)}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      Replace
                    </button>
                    <button
                      onClick={() => set(OG_IMAGE_KEY, '')}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Remove (use default)
                    </button>
                  </div>
                </div>
              )}
              {!values[OG_IMAGE_KEY] && !showOgUpload && (
                <button
                  onClick={() => setShowOgUpload(true)}
                  className="w-full p-6 border-2 border-dashed border-input rounded-lg text-muted-foreground/70 hover:border-foreground hover:text-foreground transition text-center text-sm"
                >
                  <Upload size={24} className="mx-auto mb-2" />
                  Upload share image
                </button>
              )}
              {showOgUpload && (
                <div className="border border-border rounded-lg p-4">
                  <FileUpload
                    uploadType="about-media"
                    accept="image/*"
                    multiple={false}
                    maxFiles={1}
                    onUploadComplete={(files) => {
                      const file = files[0];
                      if (file?.uploaded && file?.url) {
                        set(OG_IMAGE_KEY, file.url);
                        setShowOgUpload(false);
                      }
                    }}
                  />
                  <button
                    onClick={() => setShowOgUpload(false)}
                    className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    <X size={12} /> Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Per page */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Pages</h3>
        {PAGE_FIELDS.map((p) => {
          const tKey = `seo_${p.key}_title`;
          const dKey = `seo_${p.key}_description`;
          return (
            <div
              key={p.key}
              className="bg-card border border-border rounded-lg p-5 grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-sm font-semibold">{p.label}</span>
                    <a
                      href={`${SITE_URL}/us${p.path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block text-xs text-muted-foreground hover:text-foreground truncate"
                    >
                      {`kaay.co/us${p.path}`}
                    </a>
                  </div>
                  <GenerateButton busy={generating === p.key} onClick={() => handleGenerate(p)} />
                </div>
                <Field
                  label="Title"
                  max={TITLE_MAX}
                  value={values[tKey]}
                  onChange={(v) => set(tKey, v)}
                  placeholder={p.label}
                />
                <Field
                  label="Description"
                  max={DESC_MAX}
                  multiline
                  value={values[dKey]}
                  onChange={(v) => set(dKey, v)}
                />
              </div>
              <SnippetPreview
                url={`${SITE_URL} › us › ${p.path.replace('/', '')}`}
                siteName={siteName}
                title={values[tKey] ? `${values[tKey]} — ${siteName}` : `${p.label} — ${siteName}`}
                description={values[dKey] || values.seo_default_description}
              />
            </div>
          );
        })}
      </section>
    </div>
  );
};

export default SiteSeoTab;
